import React, { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import Nav from "../components/Nav";
import { Link } from "react-router-dom";
import { ArrowLeft, DownloadSimple, Printer, Copy, CheckCircle } from "@phosphor-icons/react";

// Generates a downloadable QR code for any URL — defaults to the Curb Crew
// landing page. Used by the admin for packaging stickers, agent flyers, etc.
const DEFAULT_URL = "https://curbcrew.co.za";

export default function BrandQR() {
  const [url, setUrl] = useState(DEFAULT_URL);
  const [label, setLabel] = useState("Curb Crew");
  const [tagline, setTagline] = useState("Smart business cards · curbcrew.co.za");
  const [copied, setCopied] = useState(false);
  const svgRef = useRef(null);

  const safeName = label.replace(/[^A-Za-z0-9_-]/g, "_") || "curbcrew";

  const downloadSVG = () => {
    const svg = svgRef.current?.querySelector("svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${safeName}_qr.svg`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadPNG = async () => {
    const svg = svgRef.current?.querySelector("svg");
    if (!svg) return;
    // Render the SVG into a 1024x1024 PNG so it's print-ready (300 dpi at ~85mm).
    const xml = new XMLSerializer().serializeToString(svg);
    const url = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const size = 1024;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      canvas.toBlob((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${safeName}_qr.png`;
        a.click();
        URL.revokeObjectURL(a.href);
      }, "image/png");
    };
    img.src = url;
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard not available */ }
  };

  return (
    <div>
      <Nav />
      <section className="border-b border-black bg-[color:var(--cc-bg-2)] no-print">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Link to="/admin/dashboard" className="label-tag inline-flex items-center gap-2" data-testid="brand-qr-back">
            <ArrowLeft size={14} weight="bold" /> Back to dashboard
          </Link>
          <h1 className="font-display font-black text-3xl tracking-tighter mt-3">Brand QR Code</h1>
          <p className="mt-1 text-sm text-neutral-700">
            Generate a clean, scannable QR for stickers, flyers, packs and packaging. Defaults to the Curb Crew home page — change the URL below for anything else (a campaign, an event flyer, etc.).
          </p>
        </div>
      </section>

      <section className="py-10 px-4">
        <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-6">
          {/* Form */}
          <div className="bg-white border border-black p-5 space-y-4 no-print">
            <div>
              <label className="label-tag">URL the QR opens</label>
              <div className="mt-1 flex gap-2">
                <input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="flex-1 px-3 py-2 border border-black outline-none font-mono-cc text-sm"
                  data-testid="brand-qr-url"
                />
                <button onClick={copyUrl} className="label-tag px-3 py-2 border border-black brutal-btn flex items-center gap-1" data-testid="brand-qr-copy">
                  <Copy size={14} weight="bold" /> {copied ? "Copied!" : "Copy"}
                </button>
              </div>
              <div className="label-tag text-neutral-500 mt-1">Test it by tapping the link from your phone before printing 500 copies.</div>
            </div>

            <div>
              <label className="label-tag">File name label</label>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-black outline-none"
                placeholder="Curb Crew"
                data-testid="brand-qr-label"
              />
            </div>

            <div>
              <label className="label-tag">Caption (shown under QR on screen only)</label>
              <input
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-black outline-none"
                data-testid="brand-qr-tagline"
              />
              <div className="label-tag text-neutral-500 mt-1">For your reference — does NOT print into the QR image file.</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2">
              <button onClick={downloadPNG} className="bg-black text-white label-tag px-3 py-3 border-2 border-black brutal-btn flex items-center justify-center gap-2" data-testid="brand-qr-download-png">
                <DownloadSimple size={16} weight="bold" /> PNG (print)
              </button>
              <button onClick={downloadSVG} className="bg-[color:var(--cc-yellow)] label-tag px-3 py-3 border-2 border-black brutal-btn flex items-center justify-center gap-2" data-testid="brand-qr-download-svg">
                <DownloadSimple size={16} weight="bold" /> SVG (designer)
              </button>
              <button onClick={() => window.print()} className="bg-white label-tag px-3 py-3 border-2 border-black brutal-btn flex items-center justify-center gap-2" data-testid="brand-qr-print">
                <Printer size={16} weight="bold" /> Print page
              </button>
            </div>

            <div className="text-xs text-neutral-600 pt-3 border-t border-dashed border-neutral-400">
              <strong>PNG</strong> = 1024×1024px, print-ready up to ~85 mm at 300 dpi. Drop it into Canva, Word, your printer's design tool.<br />
              <strong>SVG</strong> = vector, scales to any size without blurring — give this to a professional printer.<br />
              QR has the highest error-correction (level H) so it still scans even if 30% of it is scratched or covered.
            </div>
          </div>

          {/* Preview */}
          <div ref={svgRef} className="bg-white border-2 border-black p-6 flex flex-col items-center" data-testid="brand-qr-preview">
            <QRCodeSVG
              value={url || DEFAULT_URL}
              size={260}
              level="H"
              bgColor="#FFFFFF"
              fgColor="#000000"
              includeMargin={true}
            />
            <div className="mt-3 text-center">
              <div className="font-display font-black text-xl tracking-tighter">{label || "Curb Crew"}</div>
              <div className="label-tag text-neutral-700 mt-1">{tagline}</div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 25mm; }
        }
      `}</style>
    </div>
  );
}
